import type { HttpClient } from "@/shared/api/http";
import { registerPushDevice } from "@/modules/sys/api/sysApi";

const STORAGE_KEY = "galaxy.webPushDeviceToken";

export type WebPushRegisterResult =
  | "ok"
  | "denied"
  | "unsupported"
  | "error";

export function getStoredWebPushToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function ensureLocalToken(): string {
  let token = getStoredWebPushToken();
  if (!token) {
    token = `web-${crypto.randomUUID()}`;
    try {
      localStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* private mode — still register ephemeral token this session */
    }
  }
  return token;
}

/**
 * N4.1 — request Notification permission and `POST /api/v1/sys/push/devices`
 * with a stable web device token (PWA). Native FCM/APNs uses `ensurePushDevice`.
 */
export async function ensureWebPushDevice(
  http: HttpClient,
): Promise<WebPushRegisterResult> {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return "unsupported";
  }
  try {
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return "denied";

    const token = ensureLocalToken();
    await registerPushDevice(http, {
      platform: "web",
      deviceToken: token,
      channelPref: "WEB",
    });
    return "ok";
  } catch {
    return "error";
  }
}

export function webPushResultMessage(result: WebPushRegisterResult): string | null {
  switch (result) {
    case "denied":
      return "Trình duyệt từ chối quyền thông báo.";
    case "unsupported":
      return "Trình duyệt không hỗ trợ thông báo push.";
    case "error":
      return "Không đăng ký được thiết bị push.";
    default:
      return null;
  }
}
